import { useEffect, useState, useRef } from 'react';
import Image from 'next/image';
import {
  STOREFRONT_BANNER_HEIGHT_CLASSES,
  STOREFRONT_BANNER_MAX_WIDTH_CLASS,
  STOREFRONT_BANNER_MOBILE_MEDIA_QUERY,
} from '@util/bannerDimensions';
import { UserDocument } from '@util/types/firestore/users';
import dWeb from './images/shop-seller-header.png';
import cx from 'classnames';

import { VscUnmute } from "react-icons/vsc";
import { VscMute } from "react-icons/vsc";
import { VscDebugPause } from "react-icons/vsc";
import { VscPlay } from "react-icons/vsc";

const SellerProfileHeader = ({ seller }: { seller: UserDocument }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasVideo = !!seller.store_video;

  const [isScrolled, setIsScrolled] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [isHovered, setIsHovered] = useState(false);

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  useEffect(() => {
    if (!hasVideo) return;

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 30);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasVideo]);

  return (
    <div
      className={cx(
        `relative w-full ${STOREFRONT_BANNER_MAX_WIDTH_CLASS} mx-auto rounded-b-[10px] overflow-hidden transition-all duration-500`,
        {
          'h-[36rem] sm:h-[48rem]': hasVideo && !isScrolled,
          'h-[20rem] sm:h-[26rem]': hasVideo && isScrolled,
          // GB #1612 (Codex P2, second review): this is the ACTUAL storefront
          // banner box for the non-video case -- ShopSettingsPage's preview
          // now shares this exact class string via
          // STOREFRONT_BANNER_HEIGHT_CLASSES so the two can never silently
          // drift apart again.
          [STOREFRONT_BANNER_HEIGHT_CLASSES]: !hasVideo,
        }
      )}
    >
      {hasVideo ? (
        <div
          className="relative w-full h-full"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <video
            ref={videoRef}
            className="absolute inset-0 w-full h-full object-cover z-0"
            src={seller.store_video}
            muted={isMuted}
            autoPlay
            loop
            playsInline
          />

          {/* Hover Controls */}
          {isHovered && (
            <div className="absolute z-10 inset-0 flex items-end justify-center pb-[15%] sm:pb-[5%] pointer-events-none">
              <div className="flex gap-5">
                <div className="bg-black/70 rounded-[10px] p-6 pointer-events-auto">
                  <button
                    onClick={togglePlayPause}
                    className="text-white text-[20px] hover:opacity-80"
                    aria-label={isPlaying ? 'Pause video' : 'Play video'}
                  >
                    {isPlaying ? <VscDebugPause /> : <VscPlay />}
                  </button>
                </div>

                <div className="bg-black/70 rounded-[10px] p-6 flex items-center justify-center pointer-events-auto">
                  <button
                    type="button"
                    onClick={toggleMute}
                    className="text-white text-[20px] hover:opacity-80 transition-opacity duration-200 ease-in-out"
                    aria-label={isMuted ? 'Unmute video' : 'Mute video'}
                  >
                    {isMuted ? <VscMute /> : <VscUnmute />}
                  </button>
                </div>

              </div>
            </div>
          )}
        </div>
      ) : (
        // GB #1612: exactly ONE banner image may be downloaded per page
        // load -- mobile is ~65% of revenue, so fetching both is
        // unacceptable. <picture><source media> lets the BROWSER pick
        // before any JS runs (no hydration-timing double-fetch risk the
        // way a CSS hidden/sm:block toggle would have). Verified hands-on
        // with the exact DOM shape SafeImage/next-Image produces: a real
        // Playwright network trace at 390px shows exactly one request for
        // the mobile source, and at 1280px exactly one request for the
        // desktop <img>, with the non-matching source never fetched.
        // Only rendered when a mobile banner exists -- with no <source>,
        // the browser falls through to the <img> below at every width,
        // which is exactly "below 640: store_photo_mobile -> else
        // store_photo -> else dWeb".
        <picture>
          {seller.store_photo_mobile && (
            <source
              media={STOREFRONT_BANNER_MOBILE_MEDIA_QUERY}
              srcSet={seller.store_photo_mobile}
            />
          )}
          {/* GB #1612 -- Codex fifth review, finding 3 (marked serious):
              plain next/image <Image>, NOT SafeImage, on purpose.
              <picture> correctly does not fall back when a matched
              <source> fails, but that failure still fires the terminal
              <img>'s onError -- and SafeImage's handler mutates its OWN
              React state to /not_found.svg, re-synced only when the src
              PROP changes. A broken MOBILE source therefore poisoned the
              DESKTOP banner for the rest of the session: a buyer resizing
              past 640px kept seeing "not found" even though store_photo
              was never broken, because SafeImage doesn't know <picture>
              chose a different resource than the one it was given.
              Next's <Image> still renders as a real <img> (verified
              hands-on with a real Playwright network trace -- see the
              PR's picture/source composition proof) and still gets
              responsive srcset/lazy-loading, so nothing about the
              already-verified single-request-per-viewport behavior
              changes. Trade-off: a genuinely broken store_photo URL now
              shows the browser's native broken-image state instead of
              SafeImage's friendly /not_found.svg fallback -- accepted,
              since that's materially better than an unrelated mobile-
              source failure taking down a valid desktop banner. */}
          <Image
            className="object-cover"
            src={seller.store_photo ?? dWeb.src}
            key={seller.store_photo ?? 'default'}
            alt="shop seller image"
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            quality={75}
          />
        </picture>
      )}
    </div>
  );
};

export default SellerProfileHeader;